// #region Imports
import {
  ChangeDetectionStrategy, Component, DestroyRef, DOCUMENT, OnInit, WritableSignal, computed, inject, signal, untracked,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { NgxSpinnerComponent, NgxSpinnerService } from 'ngx-spinner';
import { defer, forkJoin, from, interval, Observable, of, Subscription } from 'rxjs';
import { catchError, concatMap, filter, map, take, tap } from 'rxjs/operators';

import { AppService } from './app.service';
import {
  SEARCH_MIN_LETTERS_DEFAULT,
  SITECONFIGURATION_FE_SEARCH_MIN_LETTERS,
} from './app.constants';
import { PageContent } from './classes/page-content';
import { Currency } from './models/Currency';
import { SiteThemeService } from './core/theme/site-theme.service';
import { AuthService } from './services/auth.service';
import { BootstrapService } from './services/bootstrap.service';
import { GeographyService } from './services/geography.service';
import { GeolocationService } from './services/geolocation.service';
import { IndexedDBService } from './services/indexed-db.service';
import { LanguageService } from './services/language.service';
import { OrderService } from './services/order.service';
import { PageContentService } from './services/page-content.service';
import { RmaService } from './services/rma.service';
import { ShoppingCartService } from './services/shopping-cart.service';
import { SiteConfigurationService } from './services/site-configuration.service';
import { WarehouseService } from './services/warehouse.service';
import { AppTextComponent } from './shared/base-components/layout/text/text.component';
import { AppShellComponent } from './shared/base-components/shell/shell/shell.component';
import { SafeHtmlPipe } from './shared/pipes/safe-html.pipe';
// #endregion Imports

// #region TypesAndInterfaces
// Forma delle risposte dei servizi HTTP: con un 401 il wrapper restituisce undefined, con ogni altro errore rigetta.
interface ApiResponse<T> {
  Code: number;
  Data: T;
}

// Il banner dei cookie arriva come HTML da un Content e chiama questa funzione globale per chiudersi.
interface CookieBannerWindow extends Window {
  CloseCadreAlertCookie?: () => void;
}

const HTTP_STATUS_OK: number = 200;
// Ogni quanto cambia la frase mostrata sotto lo spinner di caricamento.
const LOADER_TEXT_ROTATION_MS: number = 5000;
// Ogni quanto si cerca il pulsante di chiusura del banner cookie, che compare quando il Content è reso.
const COOKIE_BUTTON_POLL_INTERVAL_MS: number = 300;
const COOKIE_BANNER_CLOSE_BUTTON_ID: string = 'CloseCadreAlertCookieBtn';
// Primo segmento delle pagine di autenticazione, da cui non ha senso tornare dopo il login.
const AUTHENTICATION_URL_SEGMENTS: readonly string[] = ['login', 'signup', 'signup-reseller', 'login-from-admin'];
// #endregion TypesAndInterfaces

// #region ComponentDecorator
@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [NgxSpinnerComponent, AppShellComponent, AppTextComponent, SafeHtmlPipe, RouterOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
// #endregion ComponentDecorator
export class AppComponent implements OnInit {
  // #region Signals
  // Chiude il router-outlet finché utente, tabella Routing, configurazioni e colori del sito non sono pronti.
  readonly isPageBootstrapping: WritableSignal<boolean> = signal<boolean>(true);

  // Testi e personalizzazioni: mappa CType -> CValue letta dal template (banner dei cookie)
  readonly pageContents: WritableSignal<Map<string, string>> = signal(new Map<string, string>());

  // Frase mostrata sotto lo spinner, cambiata dalla rotazione a tempo
  readonly loaderText: WritableSignal<string | undefined> = signal<string | undefined>(undefined);

  // Prende il valore grezzo di localStorage: dopo la chiusura del banner è la stringa 'false',
  // che il template scarta perché confronta con == true.
  readonly showCookies: WritableSignal<boolean | string> = signal<boolean | string>(false);
  // #endregion Signals

  // #region PublicFields
  // Testi del loader: li leggono le pagine al momento di aprire i propri spinner, mai da un template.
  // Ogni caricamento assegna una Map nuova invece di modificarla sul posto.
  loaderContents: Map<string, string> = new Map<string, string>();
  // Letta solo al momento della chiamata (pagina precedente, ritorno dopo il login): nessun template la mostra
  routerHistory: string[] = [];
  // #endregion PublicFields

  // #region PrivateFields
  // Elenco dei content ricevuto dal backend, da cui si riempie pageContents
  private contentList: PageContent[] = [];
  // Elenco dei content del loader ricevuto dal backend, da cui si riempie loaderContents
  private loaderContentList: PageContent[] = [];
  // Frasi che ruotano sotto lo spinner, nell'ordine in cui si mostrano
  private loaderTexts: string[] = [];

  private currencies: Currency[] = [];
  private currency: Currency = null;

  // Attesa del token prima di inizializzare la pagina, quando all'apertura del gate non c'è ancora
  private tokenWaitSubscription: Subscription | undefined;
  private loaderTextIntervalId: ReturnType<typeof setInterval> | undefined;
  private routerHistorySubscription: Subscription | undefined;
  // Token della sessione dal signal di BootstrapService: emette a ogni suo cambiamento, senza controlli a tempo
  private readonly sessionToken$: Observable<string>;
  // #endregion PrivateFields

  // #region DependencyInjection
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly document: Document = inject(DOCUMENT);
  private readonly router: Router = inject(Router);
  private readonly appService: AppService = inject(AppService);
  private readonly rmaService: RmaService = inject(RmaService);
  private readonly indexedDBService: IndexedDBService = inject(IndexedDBService);
  private readonly bootstrapService: BootstrapService = inject(BootstrapService);
  private readonly pageContentService: PageContentService = inject(PageContentService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly shoppingCartService: ShoppingCartService = inject(ShoppingCartService);
  private readonly geolocationService: GeolocationService = inject(GeolocationService);
  private readonly geographyService: GeographyService = inject(GeographyService);
  private readonly warehouseService: WarehouseService = inject(WarehouseService);
  private readonly orderService: OrderService = inject(OrderService);
  private readonly languageService: LanguageService = inject(LanguageService);
  private readonly siteConfigurationService: SiteConfigurationService = inject(SiteConfigurationService);
  public readonly spinner: NgxSpinnerService = inject(NgxSpinnerService);
  // Il template mostra le pagine solo quando i colori del sito sono applicati:
  // non va semplificato via, è il caricamento del tema del sito.
  public readonly siteTheme: SiteThemeService = inject(SiteThemeService);
  // #endregion DependencyInjection

  // #region Constructor
  constructor() {
    this.languageService.ensureLanguage();
    this.sessionToken$ = toObservable(computed((): string => this.bootstrapService.getToken()));
  }
  // #endregion Constructor

  // #region LifecycleHooks
  ngOnInit(): void {
    // Né l'attesa del token né la rotazione dei testi del caricamento devono sopravvivere al componente.
    this.destroyRef.onDestroy((): void => {
      this.tokenWaitSubscription?.unsubscribe();
      clearInterval(this.loaderTextIntervalId);
    });

    // La cronologia parte prima dell'avvio, che naviga già verso la pagina richiesta: una pagina riservata aperta
    // a mano rimanda al login durante l'avvio, e il login deve sapere da dove si è partiti.
    this.loadRouting();

    // L'avvio scrive token, utente e cache e rinaviga: non si annulla, e il componente radice vive quanto l'app.
    this.bootstrapPage().subscribe({
      error: (error: unknown): void => {
        console.error('bootstrapPage', error);
      },
    });
  }
  // #endregion LifecycleHooks

  // #region PublicMethods
  /**
   * Avvio della pagina, ripetuto a ogni login e logout. Parte alla sottoscrizione ed emette una sola volta,
   * con il gate del router-outlet già aperto: login, login da amministratore e logout navigano dopo l'emissione.
   * I passi si susseguono in un ordine preciso; chi si sottoscrive non lo annulla, perché scrive la sessione.
   */
  bootstrapPage(): Observable<void> {
    return defer((): Observable<void> => {
      this.isPageBootstrapping.set(true);

      void this.spinner.show();

      const currentUrl = window.location.pathname + window.location.search + window.location.hash;

      if (this.bootstrapService.isFromLoginFromAdmin) {
        // Qui il sito non si risolve: il gate si apre con i colori che ci sono già.
        this.siteTheme.ensureReady();
        this.isPageBootstrapping.set(false);
        return of(undefined);
      }

      // Un errore dell'utente o della tabella Routing non deve fermare l'avvio: configurazioni di sito,
      // spinner e pagina proseguono comunque.
      return this.bootstrapService.loadUserData(currentUrl).pipe(
        catchError((error: unknown): Observable<void> => {
          console.error('bootstrapPage - loadUserData', error);
          return of(undefined);
        }),
        concatMap((): Observable<void> => this.loadSiteSettings()),
        tap((): void => {
          this.onPageBootstrapped();
        })
      );
    });
  }

  /**
   * Richiede i content generali e li applica a pageContents. La richiesta parte alla sottoscrizione,
   * emette una sola volta oppure termina in errore; con una risposta non valida i content restano quelli di prima.
   */
  getContent(): Observable<void> {
    return this.pageContentService.getContent().pipe(
      map((response: ApiResponse<PageContent[]> | undefined): void => {
        if (response && response.Code == HTTP_STATUS_OK) {
          this.contentList = response.Data;
          this.loadContent();
        }
      })
    );
  }

  logout(): void {
    this.orderService.invalidateCheckoutInitialization();
    // L'uscita è una scrittura sul server: non si lega alla vita del componente, deve arrivare comunque.
    // Anche se il backend non risponde l'uscita in locale si fa comunque, come prima.
    this.authService.logout().subscribe({
      next: (): void => {
        this.completeLogout();
      },
      error: (error: unknown): void => {
        console.error('logout - LogoutAuthentication', error);
        this.completeLogout();
      },
    });
  }

  public getPreviousUrl(): string {
    return (
      this.routerHistory[this.routerHistory.length - 2] ||
      '/' + this.appService.routeCategory
    );
  }

  /**
   * Ultima pagina visitata che non sia di autenticazione: è la pagina a cui riportare
   * l'utente dopo il login. Si scartano login e registrazione perché l'utente può passare
   * dall'una all'altra prima di accedere. Null se la cronologia non offre nulla di utile.
   */
  public getLoginReturnUrl(): string | null {
    for (let index = this.routerHistory.length - 1; index >= 0; index--) {
      const url = this.routerHistory[index];
      if (!this.isAuthenticationUrl(url)) {
        return url;
      }
    }
    return null;
  }

  public isAuthenticationUrl(url: string): boolean {
    const firstSegment = url.split(/[?#]/)[0].split('/').filter(segment => segment.length > 0)[0] ?? '';
    return AUTHENTICATION_URL_SEGMENTS.includes(firstSegment);
  }
  // #endregion PublicMethods

  // #region PrivateMethods
  private loadContent(): void {
    // Si parte dalla Map attuale: al cambio di lingua (SeoPage) i content arrivati sovrascrivono quelli presenti
    // e una chiave assente nella nuova lingua conserva il testo che aveva, come quando la Map si riempiva sul posto.
    const pageContents: Map<string, string> = new Map<string, string>(this.pageContents());
    for (const element of this.contentList) {
      pageContents.set(element.CType, element.CValue);
    }
    this.pageContents.set(pageContents);
  }

  /**
   * Colori, tabella Routing e configurazioni del sito per l'utente appena caricato. Emette una sola volta,
   * quando i tre sono pronti; un errore della tabella Routing non ferma l'avvio.
   */
  private loadSiteSettings(): Observable<void> {
    // I colori del sito dipendono dall'utente appena caricato: il loro file si scarica mentre
    // procedono tabella Routing e configurazioni, e si aspetta prima di mostrare la prima pagina.
    const siteThemeApplied: Promise<void> = this.siteTheme.applySiteTheme();

    // Le tre letture non dipendono l'una dall'altra: partono insieme e il gate si apre quando sono pronte tutte.
    return forkJoin([
      this.appService.getRoutingTable().pipe(
        catchError((error: unknown): Observable<void> => {
          console.error('bootstrapPage - getRoutingTable', error);
          return of(undefined);
        })
      ),
      // Le configurazioni di sito vanno caricate PRIMA che il router renda la prima pagina:
      // le pagine le leggono in ngOnInit, e un dizionario ancora vuoto nasconderebbe voci di menu
      // e sezioni. Il token esiste già, lo ha ottenuto loadUserData.
      this.siteConfigurationService.load(),
      // I colori per sito non sono più una classe su ion-app: SiteThemeService mette gli id del sito
      // su <html> e carica i file del registro core/theme/site-themes.registry.ts (vedi THEMING-GUIDE).
      from(siteThemeApplied),
    ]).pipe(map((): void => undefined));
  }

  /** Fine dell'avvio: apre il gate del router-outlet, spegne lo spinner e prepara l'inizializzazione della pagina. */
  private onPageBootstrapped(): void {
    this.isPageBootstrapping.set(false);
    void this.spinner.hide();

    this.loaderTextChange();

    // Il bootstrap si ripete dopo login e logout: un'attesa rimasta aperta dal giro precedente
    // non deve inizializzare la pagina una seconda volta.
    this.tokenWaitSubscription?.unsubscribe();
    this.tokenWaitSubscription = undefined;

    // Di solito il token c'è già (lo ha ottenuto loadUserData) e la pagina si inizializza subito, appena chi ha
    // chiesto l'avvio (login, logout) ha ricevuto la fine del bootstrap, come quando partiva da un timer: un
    // errore dell'inizializzazione non arriva così alla catena dell'avvio. Altrimenti si aspetta che
    // BootstrapService salvi il token.
    if (this.bootstrapService.getToken() !== '') {
      queueMicrotask((): void => {
        this.initPage();
      });
      return;
    }

    this.tokenWaitSubscription = this.sessionToken$.pipe(
      filter((token: string): boolean => token !== ''),
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((): void => {
      this.initPage();
    });
  }

  /**
   * Uscita in locale, dopo la risposta o l'errore del backend: cache, sessione, lingua conservata,
   * nuovo avvio e ritorno alla home. Un errore qui si scrive in console e non va oltre.
   */
  private completeLogout(): void {
    // L'uscita riscrive sessione e cache e riavvia l'app: non si annulla.
    defer(async (): Promise<void> => {
      const language = this.languageService.getLanguage();
      await this.indexedDBService.truncateAllTables();
      this.appService.clearLocals();
      this.bootstrapService.firstLoad();

      this.languageService.setLanguage(language);
    }).pipe(
      concatMap((): Observable<void> => this.bootstrapPage()),
      tap((): void => {
        void this.router.navigate(['/']);
      })
    ).subscribe({
      error: (error: unknown): void => {
        console.error('logout', error);
      },
    });
  }

  private loaderTextChange(): void {
    // Una nuova chiamata (bootstrap ripetuto dopo login o logout) riparte da capo invece di
    // sommare un'altra rotazione a quella già attiva.
    clearInterval(this.loaderTextIntervalId);

    let textIndex = 0;
    this.loaderText.set(this.loaderTexts[textIndex]);
    this.loaderTextIntervalId = setInterval((): void => {
      textIndex++;
      this.loaderText.set(this.loaderTexts[textIndex]);
      if (textIndex == this.loaderTexts.length - 1) {
        textIndex = -1;
      }
    }, LOADER_TEXT_ROTATION_MS);
  }

  /**
   * Aggiunge il CSS e la favicon del sito, solo quando l'utente li indica: un link vuoto
   * produrrebbe una richiesta inutile. global.scss è già compilato nel bundle degli stili.
   */
  private loadStyle(styleName: string, faviconName: string): void {
    const head = this.document.getElementsByTagName('head')[0];

    if (styleName) {
      const style = this.document.createElement('link');
      style.rel = 'stylesheet';
      style.href = `${styleName}`;
      style.type = 'text/css';

      head.appendChild(style);
    }

    if (faviconName) {
      const favicon = this.document.createElement('link');
      favicon.rel = 'icon';
      favicon.type = 'image/x-icon';
      favicon.href = `assets/icon/${faviconName}`;

      head.appendChild(favicon);
    }
  }

  private initPage(): void {
    this.languageService.ensureLanguage();

    // Il token ora esiste: se la lingua è arrivata dall'URL la si scrive anche sul profilo utente.
    // È una scrittura sul server: non si lega alla vita del componente. Emette sempre una volta sola
    // (anche in errore) e solo dopo partono le letture della pagina, come prima con l'attesa.
    this.languageService.flushPendingUserLanguage().subscribe((): void => {
      this.loadPageData();
    });
  }

  /**
   * Letture d'avvio, tutte insieme perché nessuna usa il risultato di un'altra: valute (poi valuta scelta),
   * permesso resi, content generali e testi del loader (senza attenderli). Valute e permesso resi non
   * fermano nulla se falliscono; magazzino, carrello, banner cookie e stile partono quando le tre letture
   * sono finite, e solo se i content sono arrivati.
   */
  private loadPageData(): void {
    this.getLoaderContent();

    forkJoin([
      this.loadCurrencies().pipe(
        tap((): void => {
          //catturo la posizione dell´utente e recupero la valuta
          if (!this.appService.getLocal(this.appService.localCurrency)) {
            this.geolocationService.setDefaultCurrency();
          }

          // Le configurazioni le ha già caricate bootstrapPage, l'unico punto da cui si arriva qui:
          // richiederle di nuovo raddoppierebbe le chiamate quando la cache non è stata scritta.
          this.appService.setSearchMinLetters(this.siteConfigurationService.getNumber(
            SITECONFIGURATION_FE_SEARCH_MIN_LETTERS,
            SEARCH_MIN_LETTERS_DEFAULT
          ));

          this.restoreSelectedCurrency();
        })
      ),
      this.loadRmaPermission(),
      // L'errore dei content si registra qui e non interrompe le altre due letture, che finiscono e
      // salvano il loro esito come prima.
      this.getContent().pipe(
        map((): boolean => true),
        catchError((error: unknown): Observable<boolean> => {
          console.error('initPage - getContent', error);
          return of(false);
        })
      ),
    ]).pipe(
      // Il seguito parte dentro la catena: un'eccezione arriva al ramo d'errore che la registra
      tap(([, , isContentLoaded]: [void, void, boolean]): void => {
        // Se i content non arrivano, magazzino, carrello, banner cookie e stile non si caricano, come prima.
        if (isContentLoaded) {
          this.onPageContentLoaded();
        }
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      error: (error: unknown): void => {
        console.error('initPage - getContent', error);
      },
    });
  }

  /** Recupera l'elenco delle valute selezionabili (una sola volta) e lo salva in locale. */
  private loadCurrencies(): Observable<void> {
    // L'elenco si controlla alla sottoscrizione: se c'è già non si richiede di nuovo.
    return defer((): Observable<Currency[]> =>
      !this.currencies || this.currencies.length == 0
        ? this.geographyService.getCurrencies()
        : of(this.currencies)
    ).pipe(
      map((currencies: Currency[]): void => {
        this.currencies = currencies;
        this.appService.setLocal(
          this.appService.localCurrencies,
          JSON.stringify(this.currencies)
        );
      }),
      catchError((error: unknown): Observable<void> => {
        console.error('Error retrieving currencies:', error);
        return of(undefined);
      })
    );
  }

  /**
   * Riallinea la valuta salvata in locale con l'elenco appena caricato; se non c'è o non
   * esiste più, usa quella di default.
   */
  private restoreSelectedCurrency(): void {
    if (this.appService.getLocal(this.appService.localCurrency)) {
      const notUpdatedCurrency: Currency = JSON.parse(
        this.appService.getLocal(this.appService.localCurrency)
      ) as Currency;
      this.currency = this.currencies?.filter(
        currencyItem => currencyItem.ID_Currency == notUpdatedCurrency.ID_Currency
      )[0];
      this.appService.setLocal(
        this.appService.localCurrency,
        JSON.stringify(this.currency)
      );
    }

    if (
      !this.currency ||
      !this.appService.getLocal(this.appService.localCurrency)
    ) {
      this.currency = this.currencies?.filter(currencyItem => currencyItem.Default == true)[0];
      this.appService.setLocal(
        this.appService.localCurrency,
        JSON.stringify(this.currency)
      );
    }
  }

  /** Recupera se l'utente è abilitato a gestire gli RMA e lo salva in locale. */
  private loadRmaPermission(): Observable<void> {
    return this.rmaService.userCanManageRMA().pipe(
      map((rmaPermissionResponse: ApiResponse<boolean> | undefined): void => {
        if (rmaPermissionResponse && rmaPermissionResponse.Code == HTTP_STATUS_OK) {
          // Si scrive da BootstrapService, che aggiorna anche il signal letto dalla voce Resi dell'header.
          this.bootstrapService.setRmaPermission(rmaPermissionResponse.Data ?? true);
        }
      }),
      catchError((error: unknown): Observable<void> => {
        console.error('Error retrieving RMA permission:', error);
        return of(undefined);
      })
    );
  }

  /** Quello che richiede i contenuti della pagina: magazzino, carrello, banner cookie e stile del sito. */
  private onPageContentLoaded(): void {
    this.loadDefaultWarehouseData();
    this.shoppingCartService.getShoppingCartItemsCount();

    this.initCookieBanner();

    const user = this.bootstrapService.getUser();
    if (user) {
      this.loadStyle(user.siteCss, user.siteFavicon);
    } else {
      this.loadStyle('', '');
    }
  }

  private initCookieBanner(): void {
    const cookieBannerWindow: CookieBannerWindow = window;

    this.showCookies.set(this.appService.getLocal(
      this.appService.localShowCookies
    ) ?? true);

    // Aggiungo una callback per chiudere il banner a livello di script html: la chiama codice esterno ad
    // Angular, e la scrittura nel signal basta a ridisegnare il template.
    cookieBannerWindow.CloseCadreAlertCookie = (): void => {
      this.appService.setLocal(this.appService.localShowCookies, 'false');
      this.showCookies.set(false);
    };

    if (this.showCookies()) {
      // Il pulsante fa parte dell'HTML del Content e compare solo dopo il rendering:
      // lo si cerca finché non c'è, poi ci si ferma.
      interval(COOKIE_BUTTON_POLL_INTERVAL_MS).pipe(
        map((): HTMLElement | null => this.document.getElementById(COOKIE_BANNER_CLOSE_BUTTON_ID)),
        filter((button): button is HTMLButtonElement => button !== null),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((button: HTMLButtonElement): void => {
        button.addEventListener('click', (): void => cookieBannerWindow.CloseCadreAlertCookie?.());
      });
    }
  }

  private loadDefaultWarehouseData(): void {
    if (!this.appService.getLocal(this.appService.localDefaultWarehouse)) {
      this.warehouseService.getDefaultWarehouse()
        .pipe(
          // L'esito si applica dentro la catena: un'eccezione arriva al ramo d'errore che la registra
          tap((response: ApiResponse<unknown> | undefined): void => {
            if (response && response.Code == HTTP_STATUS_OK) {
              this.appService.setLocal(this.appService.localDefaultWarehouse, JSON.stringify(response.Data));
            }
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe({
          error: (error: unknown): void => {
            console.error('loadDefaultWarehouseData', error);
          },
        });
    }
  }

  private getLoaderContent(): void {
    this.pageContentService.getContentLoader()
      .pipe(
        // I testi si applicano dentro la catena: un'eccezione arriva al ramo d'errore che la registra
        tap((response: ApiResponse<PageContent[]> | undefined): void => {
          if (response && response.Code == HTTP_STATUS_OK) {
            this.loaderContentList = response.Data;
            this.loadLoaderContent();
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        error: (error: unknown): void => {
          console.error('getLoaderContent', error);
        },
      });
  }

  private loadLoaderContent(): void {
    // Si parte dalla Map attuale, come quando si riempiva sul posto; le pagine leggono il campo solo al
    // momento di aprire uno spinner, quindi vedono sempre la Map assegnata per ultima.
    const loaderContents: Map<string, string> = new Map<string, string>(this.loaderContents);
    for (const element of this.loaderContentList) {
      loaderContents.set(element.CType, element.CValue);
    }
    this.loaderContents = loaderContents;

    this.loaderTexts = [
      this.loaderContents.get('lbl_caricamentoInCorso'),
      this.loaderContents.get('lbl_ancoraUnAttimo'),
      this.loaderContents.get('lbl_ciSiamoQuasi'),
      this.loaderContents.get('lbl_scusaciPerLAttesa'),
      this.loaderContents.get('lbl_stiamoLavorandoPerTe'),
    ];
  }

  private loadRouting(): void {
    // Una seconda sottoscrizione registrerebbe ogni navigazione due volte e getPreviousUrl
    // restituirebbe la pagina corrente.
    if (this.routerHistorySubscription) {
      return;
    }

    // Si registrano le pagine che l'utente vede nella barra degli indirizzi: le navigazioni senza cambio di URL
    // (il passaggio da '/' con cui l'avvio riattiva le rotte, le pagine aperte da una parola SEO) non sono
    // pagine a cui tornare, e quella da '/' farebbe sembrare nota una provenienza che non lo è.
    this.routerHistorySubscription = this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        filter((): boolean => untracked(this.router.currentNavigation)?.extras.skipLocationChange !== true),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ urlAfterRedirects }: NavigationEnd): void => {
        this.routerHistory = [...this.routerHistory, urlAfterRedirects];
      });
  }
  // #endregion PrivateMethods
}

export class LanguageFlag {
  public flagImageUrl: string;
  public flagImageAlt: string;
  public code: string;
  public language: string;
  public order: number;

  public constructor() {
    this.flagImageUrl = '';
    this.flagImageAlt = '';
    this.code = '';
    this.language = '';
    this.order = 0;
  }
}
